import React, { useRef } from "react";
import { DayPicker } from "react-day-picker";
import classNames from "classnames/bind";
import inputStyles from "./DateInput.module.scss";
import overlayStyles from "./DateOverlay.module.scss";
import config from "../../../config";
import FormInputWrapper from "../FormInputWrapper";
import { ControlledDateInputType, DateInputProps } from "./types";
import FormIcon from "../FormIcon";
import useDateInput from "./useDateInput";
import { getTimeStringFromDate } from "../../../utils/timeFormatter";
import CalendarFooter from "../../../DateRangeSelector/CalendarFooter";
import useOnClickOutside from "../../../utils/hooks/useOnClickOutside";

const cx = classNames.bind(inputStyles);
const overlayCx = classNames.bind(overlayStyles);

const TODAY = new Date();

/**
 * @deprecated use the new "DateSelectorInput" instead (see Storybook documentation)
 */
export const DateInput = ({
  id,
  name,
  placeholder,
  value,
  onBlur,
  onChange,
  format = config.DATE_FORMAT,
  isDisabled,
  showIcon,
  error,
  inputRef,
  disabled,
  hidden,
  isClickOutsideEnabled = true,
  isTouched,
  isTouchedRespected = false,
}: DateInputProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { inputProps, dayPickerProps, popoverProps, footerProps } =
    useDateInput({
      value: value ?? TODAY,
      onChange,
      onBlur,
      format,
      placeholder,
      isTouched,
      isTouchedRespected,
    });

  useOnClickOutside(ref, popoverProps.onClickOutside, isClickOutsideEnabled);

  return (
    <>
      <div
        className={cx("container")}
        style={{ position: "relative" }}
        ref={ref}
      >
        <input
          {...inputProps}
          id={id}
          name={name}
          className={cx("input", {
            "input--error": error,
            "input--disabled": isDisabled,
            "input--truncated": showIcon,
          })}
          placeholder={placeholder}
          ref={inputRef}
          disabled={isDisabled}
          autoComplete="off"
        />

        {showIcon && (
          <FormIcon
            isAbsolutelyPositionedToRight
            error={error}
            isDisabled={isDisabled}
            name="calendar"
          />
        )}

        {popoverProps.isOpen && (
          <div className={overlayCx("overlay")}>
            <div className={cx("calendarContainer")}>
              <DayPicker
                {...dayPickerProps}
                classNames={overlayStyles}
                formatters={{
                  formatWeekdayName: (date) =>
                    getTimeStringFromDate(date, "ddd").slice(0, 1),
                }}
                disabled={disabled}
                hidden={hidden}
              />

              <CalendarFooter
                {...footerProps}
                showDifference={false}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

/**
 * @deprecated use the new "DateSelectorInput" instead (see Storybook documentation)
 */
const ControlledDateInput = (props: ControlledDateInputType) => {
  return (
    <FormInputWrapper
      {...props}
      as={DateInput}
    />
  );
};

export default ControlledDateInput;
